import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import ImageUpload from "../ImageUpload";
import { fetchAdmin, jsonHeaders } from "../../utils/adminApi";
import { Plus, Save, Edit2, Trash2, Star } from "lucide-react";

const EMPTY_BLOG = { slug: "", title: "", excerpt: "", category: "Trek Guides", author: "Trailz Team", date: "", readTime: "", featured: false, image: "", content: "", is_active: true };

export default function BlogPanel({ blogPosts, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_BLOG);

  async function save(e) {
    e.preventDefault();
    if (editing) {
      await fetchAdmin(`/api/content/admin/blog/${editing}`, { method: "PUT", headers: jsonHeaders(), body: JSON.stringify(form) });
    } else {
      await fetchAdmin("/api/content/admin/blog", { method: "POST", headers: jsonHeaders(), body: JSON.stringify(form) });
    }
    setShowForm(false); setEditing(null); setForm(EMPTY_BLOG); onRefresh();
  }

  function edit(p) { setEditing(p.post_id); setForm({ ...p }); setShowForm(true); }

  async function remove(id) { if (!window.confirm("Delete this blog post?")) return; await fetchAdmin(`/api/content/admin/blog/${id}`, { method: "DELETE" }); onRefresh(); }

  return (
    <div data-testid="blog-panel">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-white">Blog Posts ({blogPosts.length})</h2>
        <Button onClick={() => { setShowForm(!showForm); setEditing(null); setForm({ ...EMPTY_BLOG, date: new Date().toISOString().split("T")[0] }); }} className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl" data-testid="add-blog-btn">
          <Plus className="w-4 h-4 mr-1.5" />{showForm ? "Cancel" : "Add Post"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={save} className="bg-stone-900/60 border border-stone-800 rounded-xl p-6 mb-6 space-y-4" data-testid="blog-form">
          <h3 className="text-white font-semibold">{editing ? "Edit Blog Post" : "New Blog Post"}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label className="text-stone-300">Title</Label><Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} required className="mt-1 bg-stone-800 border-stone-700 text-white rounded-xl" data-testid="blog-title-input" /></div>
            <div><Label className="text-stone-300">Slug</Label><Input value={form.slug} onChange={e => setForm(p => ({ ...p, slug: e.target.value }))} required className="mt-1 bg-stone-800 border-stone-700 text-white rounded-xl" /></div>
            <div><Label className="text-stone-300">Category</Label>
              <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="mt-1 w-full bg-stone-800 border border-stone-700 text-white rounded-xl h-10 px-3">
                {["Trek Guides", "Destination Guides", "Travel Tips", "Adventure Sports", "Spiritual Journeys"].map(c => <option key={c} value={c}>{c}</option>)}
              </select></div>
            <div><Label className="text-stone-300">Date</Label><Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="mt-1 bg-stone-800 border-stone-700 text-white rounded-xl" /></div>
            <div><Label className="text-stone-300">Read Time</Label><Input value={form.readTime} onChange={e => setForm(p => ({ ...p, readTime: e.target.value }))} className="mt-1 bg-stone-800 border-stone-700 text-white rounded-xl" placeholder="10 min read" /></div>
            <div className="sm:col-span-2"><ImageUpload value={form.image} onChange={v => setForm(p => ({ ...p, image: v }))} label="Featured Image" /></div>
          </div>
          <div><Label className="text-stone-300">Excerpt</Label><Input value={form.excerpt} onChange={e => setForm(p => ({ ...p, excerpt: e.target.value }))} className="mt-1 bg-stone-800 border-stone-700 text-white rounded-xl" /></div>
          <div><Label className="text-stone-300">Content (Markdown)</Label><Textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} className="mt-1 bg-stone-800 border-stone-700 text-white rounded-xl font-mono text-sm" rows={10} /></div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-stone-300"><input type="checkbox" checked={form.featured} onChange={e => setForm(p => ({ ...p, featured: e.target.checked }))} /> Featured</label>
            <label className="flex items-center gap-2 text-stone-300"><input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} /> Active</label>
          </div>
          <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="save-blog-btn"><Save className="w-4 h-4 mr-1.5" />{editing ? "Update Post" : "Save Post"}</Button>
        </form>
      )}

      <div className="space-y-3">
        {blogPosts.map(p => (
          <div key={p.post_id} className="bg-stone-900/60 border border-stone-800 rounded-xl p-4 flex items-center justify-between" data-testid={`blog-${p.post_id}`}>
            <div className="flex items-center gap-3">
              {p.image && <img src={p.image} alt="" className="w-14 h-10 rounded-lg object-cover hidden sm:block" />}
              <div>
                <div className="flex items-center gap-2"><h3 className="text-white font-medium text-sm">{p.title}</h3>{p.featured && <Badge className="bg-amber-500/20 text-amber-400 text-xs">Featured</Badge>}{!p.is_active && <Badge className="bg-red-500/20 text-red-400 text-xs">Hidden</Badge>}</div>
                <p className="text-stone-500 text-xs">{p.category} | {p.date} | {p.readTime}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => edit(p)} variant="ghost" size="sm" className="text-stone-400 hover:text-amber-400"><Edit2 className="w-4 h-4" /></Button>
              <Button onClick={() => remove(p.post_id)} variant="ghost" size="sm" className="text-stone-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
        {blogPosts.length === 0 && <p className="text-stone-500 text-center py-8">No blog posts yet</p>}
      </div>
    </div>
  );
}
